// admin/lancar.js — lançamento manual de frequência (Fase 6 + envio real na Fase 7).
//
// Origem (monólito): linhas 9492-9795 (mboAdminInit, mboAdminOpenEdit,
//   mboAdminCloseEdit, mboAdminSelectStatus, mboAdminSaveStatus,
//   mboAdminAlterarStatus) — adaptados para módulo sem variáveis globais.
//
// Fluxo (v19):
//   1. Admin seleciona turma + data.
//   2. Marca status de cada aluno (presente/falta/justificado/atrasado).
//   3. Cada mudança é salva IMEDIATAMENTE em storage local (rascunho).
//   4. Botão "Enviar ao servidor" abre o modal de revisão (lancar-enviar.js)
//      e, se confirmado, faz POST para o Apps Script (aba FREQUENCIA_ADMIN).
//   5. Após POST com sucesso, o rascunho é limpo.
//
// Dependências:
//   - api     (core/api.js)    — api.get('alunos')
//   - auth    (core/auth.js)   — auth.getCurrent()
//   - notify  (core/notify.js) — notify.success(), notify.error()
//   - router  (core/router.js) — router.go('/admin')
//   - storage (core/storage.js)— storage.get/set para rascunho
//
// Exporta:
//   function render(container)

import { api }     from '../core/api.js';
import { auth }    from '../core/auth.js';
import { notify }  from '../core/notify.js';
import { router }  from '../core/router.js';
import { storage } from '../core/storage.js';
import { abrirModalEnviar } from './lancar-enviar.js';

// ── Chave de storage do rascunho ─────────────────────────────────────────────

const RASCUNHO_KEY = 'admin:lancamento:rascunho';

// ── Status disponíveis ────────────────────────────────────────────────────────

const STATUS_OPCOES = [
  { valor: 'presente',    emoji: '✅', titulo: 'Presente',    desc: 'Aluno compareceu à aula' },
  { valor: 'falta',       emoji: '❌', titulo: 'Falta',       desc: 'Aluno não compareceu' },
  { valor: 'justificado', emoji: '📋', titulo: 'Justificado', desc: 'Falta com justificativa aceita' },
  { valor: 'atrasado',    emoji: '⏰', titulo: 'Atrasado',    desc: 'Chegou após o início da aula' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Lê o rascunho do storage. */
function lerRascunho() {
  return storage.get(RASCUNHO_KEY, { data: null, turma: null, registros: {} });
}

/** Salva o rascunho no storage. */
function salvarRascunho(rascunho) {
  storage.set(RASCUNHO_KEY, rascunho);
}

/** Retorna o status atual de um aluno no rascunho (ou null). */
function statusNoRascunho(rascunho, cpf) {
  return rascunho.registros[cpf] || null;
}

/** Formata data YYYY-MM-DD para DD/MM/YYYY. */
function formatarData(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

// ── Render do modal de edição de status ─────────────────────────────────────

/**
 * Abre modal inline de seleção de status para um aluno.
 * @param {HTMLElement} container  - raiz do app
 * @param {object}      aluno      - { nome, turma, senha }
 * @param {string}      dataAula   - ISO YYYY-MM-DD
 * @param {string|null} statusAtual
 * @param {Function}    onSalvar   - callback(cpf, novoStatus)
 */
function abrirModalStatus(container, aluno, dataAula, statusAtual, onSalvar) {
  // Remove overlay anterior se existir
  const prev = document.getElementById('lancarModalOverlay');
  if (prev) prev.remove();

  const opcoesHTML = STATUS_OPCOES.map(op => `
    <button
      class="lancar-status-opcao"
      data-status="${op.valor}"
      style="
        display:flex;align-items:center;gap:12px;padding:14px 16px;
        border-radius:12px;border:2px solid ${op.valor === statusAtual ? '#B3002D' : '#E8E8E8'};
        background:${op.valor === statusAtual ? 'rgba(179,0,45,0.08)' : '#fff'};
        cursor:pointer;width:100%;text-align:left;font-family:'Montserrat',sans-serif;
        transition:all 0.2s;
      "
      type="button"
    >
      <span style="font-size:22px">${op.emoji}</span>
      <div style="flex:1">
        <div style="font-size:14px;font-weight:700;color:#0A0A0A">${op.titulo}</div>
        <div style="font-size:11px;color:#9B9B9B;margin-top:2px">${op.desc}</div>
      </div>
      <div style="
        width:22px;height:22px;border-radius:50%;flex-shrink:0;
        display:flex;align-items:center;justify-content:center;
        border:2px solid ${op.valor === statusAtual ? '#B3002D' : '#E8E8E8'};
        background:${op.valor === statusAtual ? 'linear-gradient(90deg,#6A001F,#B3002D)' : 'transparent'};
        color:${op.valor === statusAtual ? '#fff' : 'transparent'};font-size:12px;
      ">✓</div>
    </button>`).join('');

  const overlay = document.createElement('div');
  overlay.id = 'lancarModalOverlay';
  overlay.style.cssText = `
    position:fixed;inset:0;background:rgba(10,10,10,0.7);z-index:300;
    display:flex;align-items:center;justify-content:center;
    backdrop-filter:blur(4px);
  `;
  overlay.innerHTML = `
    <div style="
      background:#fff;border-radius:20px;width:90%;max-width:400px;
      max-height:80vh;overflow-y:auto;
      box-shadow:0 24px 80px rgba(0,0,0,0.3);
    ">
      <div style="background:linear-gradient(90deg,#6A001F,#B3002D);padding:20px;border-radius:20px 20px 0 0;position:relative">
        <button id="lancarModalFechar" style="
          position:absolute;top:12px;right:12px;width:32px;height:32px;
          border-radius:50%;border:none;background:rgba(255,255,255,0.2);
          color:#fff;font-size:16px;cursor:pointer;
        ">✕</button>
        <div style="font-size:18px;font-weight:700;color:#fff">${aluno.nome.split(' ')[0]}</div>
        <div style="font-size:12px;color:rgba(255,255,255,0.7)">Turma ${aluno.turma} · ${formatarData(dataAula)}</div>
      </div>
      <div style="padding:20px">
        <div style="font-size:12px;font-weight:700;color:#9B9B9B;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px">
          Selecione o status
        </div>
        <div style="display:flex;flex-direction:column;gap:10px;margin-bottom:20px">
          ${opcoesHTML}
        </div>
        <div style="display:flex;gap:10px">
          <button id="lancarModalCancelar" style="
            flex:1;padding:14px;border-radius:12px;background:#F5F5F5;
            color:#6B6B6B;border:none;font-family:'Montserrat',sans-serif;
            font-size:14px;font-weight:700;cursor:pointer;
          ">Cancelar</button>
          <button id="lancarModalSalvar" style="
            flex:1;padding:14px;border-radius:12px;
            background:linear-gradient(90deg,#6A001F,#B3002D);
            color:#fff;border:none;font-family:'Montserrat',sans-serif;
            font-size:14px;font-weight:700;cursor:pointer;opacity:0.4;cursor:not-allowed;
          " disabled>Salvar</button>
        </div>
      </div>
    </div>`;

  container.appendChild(overlay);

  let selecionado = statusAtual;
  const salvarBtn = overlay.querySelector('#lancarModalSalvar');

  // Seleção de opção
  overlay.querySelectorAll('.lancar-status-opcao').forEach(btn => {
    btn.addEventListener('click', () => {
      selecionado = btn.dataset.status;
      overlay.querySelectorAll('.lancar-status-opcao').forEach(b => {
        const ativo = b.dataset.status === selecionado;
        b.style.borderColor = ativo ? '#B3002D' : '#E8E8E8';
        b.style.background  = ativo ? 'rgba(179,0,45,0.08)' : '#fff';
        b.querySelector('div:last-child').style.background = ativo
          ? 'linear-gradient(90deg,#6A001F,#B3002D)' : 'transparent';
        b.querySelector('div:last-child').style.color = ativo ? '#fff' : 'transparent';
        b.querySelector('div:last-child').style.borderColor = ativo ? '#B3002D' : '#E8E8E8';
      });
      const diferente = selecionado !== statusAtual;
      salvarBtn.disabled  = !diferente;
      salvarBtn.style.opacity = diferente ? '1' : '0.4';
      salvarBtn.style.cursor  = diferente ? 'pointer' : 'not-allowed';
    });
  });

  // Fechar
  const fechar = () => overlay.remove();
  overlay.querySelector('#lancarModalFechar').addEventListener('click', fechar);
  overlay.querySelector('#lancarModalCancelar').addEventListener('click', fechar);
  overlay.addEventListener('click', e => { if (e.target === overlay) fechar(); });

  // Salvar
  salvarBtn.addEventListener('click', () => {
    if (!selecionado || selecionado === statusAtual) return;
    onSalvar(aluno.senha, selecionado);
    fechar();
  });
}

// ── Render principal ──────────────────────────────────────────────────────────

export async function render(container) {
  container.innerHTML = `
    <div style="min-height:100vh;background:#F5F5F5;font-family:'Montserrat',sans-serif;">
      <header style="background:linear-gradient(90deg,#6A001F,#B3002D);padding:calc(env(safe-area-inset-top,0px)+16px) 20px 16px;position:sticky;top:0;z-index:100">
        <div style="display:flex;align-items:center;gap:14px">
          <button id="lancarBackBtn" style="width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,0.18);color:#fff;font-size:18px;cursor:pointer;flex-shrink:0">←</button>
          <div>
            <div style="font-size:10px;font-weight:600;color:rgba(255,255,255,0.65);letter-spacing:2px;text-transform:uppercase">Admin</div>
            <div style="font-size:20px;font-weight:800;color:#fff">Lançar Frequência</div>
          </div>
        </div>
      </header>

      <!-- Banner Fase 7: envio ativo -->
      <div style="background:#34c759;padding:10px 20px;display:flex;align-items:center;gap:8px">
        <span style="font-size:16px">☁️</span>
        <div style="font-size:12px;font-weight:700;color:#fff">
          Rascunho salvo localmente — use "Enviar ao servidor" para gravar na planilha
        </div>
      </div>

      <main style="padding:20px;padding-bottom:40px">
        <!-- Controles: turma + data -->
        <div style="background:#fff;border-radius:16px;padding:16px;box-shadow:0 2px 12px rgba(0,0,0,0.07);margin-bottom:16px">
          <div style="font-size:11px;font-weight:800;color:#9B9B9B;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px">Configurar lançamento</div>
          <div style="display:flex;gap:12px;flex-wrap:wrap">
            <div style="flex:1;min-width:120px">
              <label style="font-size:11px;font-weight:700;color:#6B6B6B;display:block;margin-bottom:4px">Turma</label>
              <select id="lancarTurmaSelect" style="width:100%;padding:10px;border-radius:10px;border:1.5px solid #E8E8E8;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:600;color:#0A0A0A;background:#fff">
                <option value="">Carregando...</option>
              </select>
            </div>
            <div style="flex:1;min-width:140px">
              <label style="font-size:11px;font-weight:700;color:#6B6B6B;display:block;margin-bottom:4px">Data da aula</label>
              <input id="lancarDataInput" type="date" value="${new Date().toISOString().slice(0, 10)}"
                style="width:100%;padding:10px;border-radius:10px;border:1.5px solid #E8E8E8;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:600;color:#0A0A0A;box-sizing:border-box">
            </div>
          </div>
        </div>

        <div id="lancarListaContainer">
          <div style="text-align:center;padding:40px 20px;color:#9B9B9B;font-size:13px">Selecione turma e data acima.</div>
        </div>
      </main>
    </div>`;

  container.querySelector('#lancarBackBtn').addEventListener('click', () => router.go('/admin'));

  // Carrega lista de alunos
  let alunosList = [];
  try {
    const cached = storage.get('alunos_cache', null);
    if (cached && Array.isArray(cached.alunos)) {
      alunosList = cached.alunos;
    } else {
      alunosList = await api.get('alunos');
    }
  } catch (e) {
    notify.error('Não foi possível carregar a lista de alunos.');
    console.error('[lancar] erro ao buscar alunos:', e);
  }

  // Guard: se a rota mudou durante o await (ex.: logout, navegação),
  // o innerHTML do container pode ter sido substituído por outro render.
  // O select deste módulo não existirá mais. Aborta silenciosamente.
  const selectTurma = container.querySelector('#lancarTurmaSelect');
  if (!selectTurma) return;

  // Normaliza: a API retorna `cpf`, mas o auth interno usa `senha`. Garantimos
  // que ambos existam apontando para o mesmo valor — evita bugs de chave.
  const alunos = (Array.isArray(alunosList) ? alunosList : [])
    .filter(a => a.turma !== 'Admin')
    .map(a => ({ ...a, senha: a.senha || a.cpf, cpf: a.cpf || a.senha }));

  // Popula select de turmas
  const turmas = [...new Set(alunos.map(a => a.turma))].sort((a, b) => Number(a) - Number(b));
  selectTurma.innerHTML = `<option value="">— Selecione a turma —</option>` +
    turmas.map(t => `<option value="${t}">Turma ${t}</option>`).join('');

  const listaContainer = container.querySelector('#lancarListaContainer');

  function renderLista() {
    const turma = selectTurma.value;
    const data  = container.querySelector('#lancarDataInput').value;
    if (!turma || !data) {
      listaContainer.innerHTML = `<div style="text-align:center;padding:40px 20px;color:#9B9B9B;font-size:13px">Selecione turma e data acima.</div>`;
      return;
    }

    const rascunho = lerRascunho();
    const alunosTurma = alunos.filter(a => a.turma === turma);

    if (!alunosTurma.length) {
      listaContainer.innerHTML = `<div style="text-align:center;padding:40px 20px;color:#9B9B9B;font-size:13px">Nenhum aluno nesta turma.</div>`;
      return;
    }

    // Contagem de registros
    const qtdLancados = alunosTurma.filter(a => rascunho.registros[a.senha]).length;

    const statusHTML = (cpf) => {
      const s = rascunho.registros[cpf];
      if (!s) return `<span style="font-size:10px;font-weight:700;color:#9B9B9B;background:#F5F5F5;padding:3px 10px;border-radius:100px">—</span>`;
      const cfg = { presente:'#34c759', falta:'#ff3b30', justificado:'#ff9500', atrasado:'#007AFF' };
      const bg  = { presente:'#e8f5e9', falta:'#fce4ec', justificado:'#fff3e0', atrasado:'#e8f0ff' };
      const label = { presente:'Presente', falta:'Falta', justificado:'Justificado', atrasado:'Atrasado' };
      return `<span style="font-size:10px;font-weight:700;color:${cfg[s]};background:${bg[s]};padding:3px 10px;border-radius:100px">${label[s]}</span>`;
    };

    listaContainer.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
        <div style="font-size:13px;font-weight:700;color:#0A0A0A">Turma ${turma} · ${formatarData(data)}</div>
        <div style="font-size:12px;color:#9B9B9B">${qtdLancados}/${alunosTurma.length} lançados</div>
      </div>
      <div style="background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.07)">
        ${alunosTurma.map((aluno, idx) => {
          const initials = aluno.nome.split(' ').slice(0, 2).map(n => n[0]).join('');
          return `
            <div class="lancar-row" data-cpf="${aluno.senha}" style="
              display:flex;align-items:center;gap:12px;padding:14px 16px;
              border-bottom:1px solid #F5F5F5;cursor:pointer;
              transition:background 0.15s;background:${idx % 2 === 0 ? '#fff' : '#FAFAFA'};
            ">
              <div style="
                width:34px;height:34px;border-radius:50%;
                background:linear-gradient(135deg,#6A001F,#B3002D);
                display:flex;align-items:center;justify-content:center;
                font-size:11px;font-weight:900;color:#fff;flex-shrink:0;
              ">${initials}</div>
              <div style="flex:1;min-width:0">
                <div style="font-size:13px;font-weight:700;color:#0A0A0A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${aluno.nome}</div>
              </div>
              <div class="lancar-status-badge">${statusHTML(aluno.senha)}</div>
              <div style="color:#9B9B9B;font-size:14px">›</div>
            </div>`;
        }).join('')}
      </div>
      <div style="margin-top:14px;display:flex;gap:10px;flex-wrap:wrap">
        <button id="lancarSalvarBtn" style="
          flex:1;min-width:150px;padding:13px;background:#fff;color:#6A001F;
          border:1.5px solid #6A001F;border-radius:12px;
          font-family:'Montserrat',sans-serif;font-size:13px;font-weight:700;cursor:pointer
        ">💾 Salvar rascunho local</button>
        <button id="lancarEnviarBtn" style="
          flex:2;min-width:170px;padding:13px;
          background:linear-gradient(90deg,#6A001F,#B3002D);color:#fff;
          border:none;border-radius:12px;
          font-family:'Montserrat',sans-serif;font-size:14px;font-weight:800;cursor:pointer
        ">☁️ Enviar ao servidor</button>
      </div>`;

    // Evento de clique nas linhas
    listaContainer.querySelectorAll('.lancar-row').forEach(row => {
      row.addEventListener('click', () => {
        const cpf = row.dataset.cpf;
        const aluno = alunos.find(a => a.senha === cpf);
        const rasc  = lerRascunho();
        abrirModalStatus(container, aluno, data, rasc.registros[cpf] || null, (cpfAlvo, novoStatus) => {
          const r = lerRascunho();
          r.registros[cpfAlvo] = novoStatus;
          r.data  = data;
          r.turma = turma;
          salvarRascunho(r);
          renderLista(); // re-render após salvar
        });
      });
    });

    // Salvar rascunho completo (apenas reforça o localStorage + toast)
    listaContainer.querySelector('#lancarSalvarBtn').addEventListener('click', () => {
      const r = lerRascunho();
      if (!Object.keys(r.registros).length) {
        notify.error('Nenhum status lançado ainda.');
        return;
      }
      notify.success(`Rascunho salvo — ${Object.keys(r.registros).length} aluno(s) registrados`);
    });

    // Enviar ao servidor (Fase 7)
    listaContainer.querySelector('#lancarEnviarBtn').addEventListener('click', () => {
      const r = lerRascunho();
      if (!Object.keys(r.registros).length) {
        notify.error('Nenhum status lançado ainda.');
        return;
      }
      const u = auth.getCurrent();
      const admin = { cpf: u?.cpf || u?.senha || '', nome: u?.nome || 'Administrador' };
      const alunosTurma = alunos.filter(a => a.turma === turma);
      abrirModalEnviar({
        container, admin, rascunho: r, alunosTurma,
        onSucesso: () => {
          // Limpa rascunho e re-renderiza
          salvarRascunho({ data: null, turma: null, registros: {} });
          renderLista();
        },
      });
    });
  }

  selectTurma.addEventListener('change', renderLista);
  container.querySelector('#lancarDataInput').addEventListener('change', renderLista);
}
